import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    X, CreditCard, User, Phone, Mail, DollarSign,
    CheckCircle, Copy, MessageCircle, Loader2, ChevronRight,
    ArrowRight, RefreshCw, UserPlus, Wallet, Info, Zap,
    Apple, Smartphone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ===== MERU CARD VISUAL =====
const MeruCardDisplay = ({ name = "", amount = 0 }) => (
    <div className="relative w-full max-w-[300px] mx-auto select-none">
        {/* Glow shadow */}
        <div className="absolute inset-0 rounded-2xl blur-2xl opacity-60 translate-y-6 scale-90"
            style={{ background: "linear-gradient(135deg, #1B6B1B 0%, #2E8B2E 100%)" }} />
        {/* Card */}
        <div className="relative rounded-2xl overflow-hidden text-white"
            style={{
                background: "linear-gradient(145deg, #0d2b0d 0%, #1B6B1B 50%, #2E8B2E 100%)",
                boxShadow: "0 20px 60px rgba(27,107,27,0.4), inset 0 1px 0 rgba(255,255,255,0.15)"
            }}>
            {/* Top glow line */}
            <div className="absolute top-0 left-0 right-0 h-px"
                style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)" }} />
            {/* Waves background */}
            <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(255,255,255,0.1) 20px, rgba(255,255,255,0.1) 21px)"
            }} />

            <div className="relative p-5">
                {/* Header */}
                <div className="flex justify-between items-start mb-5">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[3px] text-green-300">BVG LinkAmazon</p>
                        <p className="text-lg font-black mt-0.5 tracking-wide">Meru Card</p>
                    </div>
                    {/* Meru-style logo */}
                    <div className="text-right">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ background: "rgba(255,255,255,0.15)" }}>
                            <Wallet className="w-5 h-5 text-green-200" />
                        </div>
                    </div>
                </div>

                {/* Chip */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-7 rounded-md"
                        style={{ background: "linear-gradient(135deg, #c8a850, #f0d060, #c8a850)", boxShadow: "inset 0 1px 2px rgba(255,255,255,0.3)" }} />
                    <div className="text-xs font-mono text-green-300 tracking-widest opacity-70">•••• •••• ••••</div>
                </div>

                {/* Balance */}
                <div className="mb-4">
                    <p className="text-[9px] text-green-400 uppercase tracking-widest mb-0.5">Rechaj USD</p>
                    <p className="text-2xl font-black" style={{ color: "#7eff7e" }}>
                        ${amount > 0 ? parseFloat(amount).toFixed(2) : "0.00"}
                        <span className="text-xs font-medium text-green-300 ml-1">USD</span>
                    </p>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-end">
                    <div>
                        <p className="text-[9px] text-green-500 uppercase tracking-wider">Kliyan</p>
                        <p className="text-xs font-bold text-green-200">{name || "KLIYAN MERU"}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[9px] text-green-500 uppercase">Sèvis</p>
                        <p className="text-sm font-black text-green-200">MERU.HT</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// ===== HOW IT WORKS BANNER =====
const HowItWorks = () => (
    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2.5">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <p className="text-xs font-black text-blue-800 uppercase tracking-wider">Kijan sa fonksyone?</p>
        </div>
        <div className="space-y-1.5">
            {[
                { icon: "1️⃣", text: "Ou soumèt demann rechaj la ak kont Meru ou an" },
                { icon: "2️⃣", text: "Ou peye nou en HTG via MonCash" },
                { icon: "3️⃣", text: "Ou voye screenshot prèv peman sou WhatsApp" },
                { icon: "4️⃣", text: "Nou rechaje kat Meru ou an nan 15-30 minit" },
            ].map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                    <span className="text-sm flex-shrink-0">{s.icon}</span>
                    <p className="text-xs text-blue-700 leading-relaxed">{s.text}</p>
                </div>
            ))}
        </div>

        {/* Download App Links */}
        <div className="mt-4 pt-4 border-t border-blue-200/50">
            <p className="text-[9px] font-black text-blue-600 uppercase tracking-[2px] mb-2.5 text-center">📲 POKO GEN KONT MERU?</p>
            <div className="flex gap-2">
                <a href="https://apps.apple.com/co/app/meru-cuenta-en-d%C3%B3lares/id1636697895"
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 h-10 bg-[#232F1A] hover:bg-[#1a2313] text-white rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-md active:scale-95">
                    <Apple className="w-4 h-4" />
                    <span className="text-[10px] font-black tracking-tight">App Store</span>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.getmeru.app"
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 h-10 bg-[#232F1A] hover:bg-[#1a2313] text-white rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-md active:scale-95">
                    <Smartphone className="w-4 h-4" />
                    <span className="text-[10px] font-black tracking-tight">Google Play</span>
                </a>
            </div>
        </div>
    </div>
);

// ===== STEP 1: REQUEST FORM =====
const StepForm = ({ onSubmit, serviceInfo, loading }) => {
    const [clientName, setClientName] = useState("");
    const [clientPhone, setClientPhone] = useState("");
    const [meruCryptoAddress, setMeruCryptoAddress] = useState("");
    const [amountUsd, setAmountUsd] = useState("");
    const [needsAccount, setNeedsAccount] = useState(false);

    const QUICK_AMOUNTS = [5, 10, 20, 50];
    const rate = serviceInfo?.usd_to_htg_rate || 148;
    const feePct = serviceInfo?.service_fee_percent || 0.05;
    const amt = parseFloat(amountUsd) || 0;
    const feeUsd = parseFloat((amt * feePct).toFixed(2));
    const totalUsd = parseFloat((amt + feeUsd).toFixed(2));
    const totalHtg = parseInt(totalUsd * rate);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!clientName.trim()) return toast.error("Tanpri mete non ou");
        if (!clientPhone.trim()) return toast.error("Tanpri mete nimewo telefòn ou");
        if (!amt || amt < 5) return toast.error("Minimòm rechaj se $5 USD");
        if (!needsAccount && meruCryptoAddress && !meruCryptoAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
            return toast.error("Adrès resepsyon Meru ou an dwe kòmanse ak 0x epi gen 42 karaktè");
        }

        onSubmit({
            client_name: clientName.trim(),
            client_phone: clientPhone.trim(),
            meru_crypto_address: meruCryptoAddress.trim() || null,
            amount_usd: amt,
            needs_account_creation: needsAccount
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <MeruCardDisplay name={clientName} amount={amountUsd} />
            <HowItWorks />

            <div className="space-y-4">
                {/* Client Name */}
                <div>
                    <Label className="text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5" /> Non Konplè Ou
                    </Label>
                    <Input placeholder="Egzanp: Jean Pierre"
                        value={clientName} onChange={e => setClientName(e.target.value)}
                        className="h-11 border-gray-200 focus:border-green-500 rounded-xl text-sm" />
                </div>

                {/* Client Phone */}
                <div>
                    <Label className="text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> Nimewo Telefòn Ou (pou kontakte)
                    </Label>
                    <Input placeholder="Egzanp: 38574639"
                        value={clientPhone} onChange={e => setClientPhone(e.target.value)}
                        className="h-11 border-gray-200 focus:border-green-500 rounded-xl text-sm" />
                </div>

                {/* Meru account section */}
                <div className="rounded-2xl border-2 overflow-hidden transition-all"
                    style={{ borderColor: needsAccount ? "#1B6B1B" : "#e5e7eb" }}>
                    {/* Toggle needs account */}
                    <button type="button" onClick={() => setNeedsAccount(!needsAccount)}
                        className={`w-full flex items-center justify-between p-3.5 transition-all ${needsAccount ? "bg-green-50" : "bg-gray-50"}`}>
                        <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                                style={{ background: needsAccount ? "#1B6B1B" : "#e5e7eb" }}>
                                <UserPlus className={`w-4 h-4 ${needsAccount ? "text-white" : "text-gray-400"}`} />
                            </div>
                            <div className="text-left">
                                <p className={`text-sm font-black ${needsAccount ? "text-green-800" : "text-gray-700"}`}>
                                    Mwen bezwen yon nouvo kont Meru
                                </p>
                                <p className="text-[10px] text-gray-400">Nou ap kreye kont la pou ou</p>
                            </div>
                        </div>
                        <div className={`w-11 h-6 rounded-full transition-all flex items-center px-1 ${needsAccount ? "bg-green-600" : "bg-gray-300"}`}>
                            <div className={`w-4 h-4 rounded-full bg-white shadow transition-all ${needsAccount ? "translate-x-5" : "translate-x-0"}`} />
                        </div>
                    </button>

                    {/* Existing account fields */}
                    {!needsAccount && (
                        <div className="p-3.5 pt-2 space-y-3 border-t border-gray-100 bg-white">
                            <p className="text-[11px] text-gray-500 font-semibold">Adrès krip Polygon ou (Opsyonèl):</p>
                            <div>
                                <Label className="text-xs font-black text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                    <Wallet className="w-3 h-3" /> Adrès Polygon (USDT) - Opsyonèl
                                </Label>
                                <Input placeholder="0x123..."
                                    value={meruCryptoAddress} onChange={e => setMeruCryptoAddress(e.target.value)}
                                    className="h-10 border-gray-200 focus:border-green-500 rounded-xl text-sm" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Amount */}
                <div>
                    <Label className="text-xs font-black text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5" /> Kantite Rechaj (USD)
                    </Label>
                    <div className="flex gap-2 mb-2">
                        {QUICK_AMOUNTS.map(a => (
                            <button key={a} type="button" onClick={() => setAmountUsd(String(a))}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-black transition-all border ${amountUsd === String(a)
                                    ? "bg-green-600 text-white border-green-600 shadow-md"
                                    : "bg-gray-50 text-gray-600 border-gray-200 hover:border-green-400"}`}>
                                ${a}
                            </button>
                        ))}
                    </div>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-black text-gray-300">$</span>
                        <Input type="number" placeholder="0.00"
                            value={amountUsd} onChange={e => setAmountUsd(e.target.value)}
                            className="h-13 pl-8 border-gray-200 focus:border-green-500 rounded-xl text-xl font-black"
                            min={5} max={1000} step={0.01} />
                    </div>

                    {/* Cost breakdown */}
                    {amt > 0 && (
                        <div className="mt-3 bg-gray-50 rounded-xl p-3 space-y-1.5 border border-gray-100">
                            <div className="flex justify-between text-xs text-gray-500">
                                <span>Rechaj Meru</span>
                                <span className="font-bold">${amt.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-xs text-gray-500">
                                <span>Frè sèvis ({(feePct * 100).toFixed(0)}%)</span>
                                <span className="font-bold">${feeUsd.toFixed(2)}</span>
                            </div>
                            <div className="h-px bg-gray-200 my-1" />
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-gray-700">Total USD</span>
                                <span className="font-black text-gray-900">${totalUsd.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-black text-green-700 flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3" /> Ou peye (HTG @ {rate})
                                </span>
                                <span className="font-black text-lg text-green-700">{totalHtg.toLocaleString()} HTG</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Button type="submit" disabled={loading}
                className="w-full h-12 rounded-xl font-black text-base shadow-lg text-white"
                style={{ background: loading ? "#9ca3af" : "linear-gradient(135deg, #1B6B1B, #2E8B2E)" }}>
                {loading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ap trete...</>
                    : <><Zap className="w-4 h-4 mr-2" />Soumèt Demann Rechaj — ${amt > 0 ? totalUsd.toFixed(2) : "0.00"}<ArrowRight className="w-4 h-4 ml-2" /></>
                }
            </Button>
        </form>
    );
};

// ===== STEP 2: PAYMENT INSTRUCTIONS =====
const StepPayment = ({ result, onClose }) => {
    const info = result?.payment_info;
    const [isPaying, setIsPaying] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState("pending");

    const copy = (text) => {
        navigator.clipboard.writeText(String(text));
        toast.success("Kopye!");
    };

    const handleMonCashPayment = async () => {
        setIsPaying(true);
        try {
            const { data } = await axios.post(`${API}/meru/recharge/${result.request_id}/pay-moncash`);
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            }
        } catch (err) {
            toast.error("Erè nan prepare peman MonCash la");
            console.error(err);
        } finally {
            setIsPaying(false);
        }
    };

    const verifyPayment = async () => {
        setIsVerifying(true);
        try {
            const { data } = await axios.get(`${API}/meru/recharge/${result.request_id}/verify-payment`);
            if (data.success) {
                setPaymentStatus(data.status);
                toast.success("Peman ou resevwa avèk siksè!");
            } else {
                toast.error(data.message || "Peman an poko konfime");
            }
        } catch (err) {
            toast.error("Erè nan verifye peman an");
        } finally {
            setIsVerifying(false);
        }
    };

    if (paymentStatus === "payment_received" || paymentStatus === "completed" || paymentStatus === "recharged") {
        return (
            <div className="space-y-6 py-4 text-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-xl">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2">Peman Resevwa!</h3>
                    <p className="text-gray-500 text-sm leading-relaxed px-4">
                        Mèsi! Nou resevwa peman ou an. Nap rechaje kat Meru ou an nan 15-30 minit. Ou ka fèmen fenèt sa a kounye a.
                    </p>
                </div>
                <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
                    <p className="text-xs font-bold text-green-800 uppercase tracking-widest mb-1">Referans</p>
                    <p className="font-black text-green-900 text-lg">{result?.request_number}</p>
                </div>
                <Button onClick={onClose} className="w-full h-12 rounded-xl font-black bg-green-600 hover:bg-green-700 text-white shadow-lg">
                    Fèmen
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Success */}
            <div className="rounded-2xl p-5 text-center"
                style={{ background: "linear-gradient(135deg, #1B6B1B, #2E8B2E)" }}>
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-2">
                    <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <p className="font-black text-white text-lg">Demann Kreye!</p>
                <p className="text-green-200 text-sm mt-0.5 font-bold">{result?.request_number}</p>
                <p className="text-green-300 text-[11px] mt-1">Chwazi fason ou vle peye a</p>
            </div>

            {/* MonCash API Button */}
            <Button
                onClick={handleMonCashPayment}
                disabled={isPaying}
                className="w-full h-14 rounded-2xl font-black text-lg shadow-xl flex items-center justify-center gap-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: "linear-gradient(135deg, #D10A3B, #9E082D)" }}
            >
                {isPaying ? (
                    <><Loader2 className="w-6 h-6 animate-spin" /> Ap konekte...</>
                ) : (
                    <><Zap className="w-6 h-6" /> Peye ak MonCash Portal</>
                )}
            </Button>

            <div className="relative">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-400 font-bold">oswa manyèlman</span></div>
            </div>

            {/* Manual Instructions Accordion-style or direct */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-3">
                <div className="flex justify-between items-center">
                    <p className="text-xs font-black text-gray-500 uppercase tracking-widest">Enfòmasyon Transfè</p>
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                        <Phone className="w-2.5 h-2.5" /> MonCash
                    </div>
                </div>

                <div className="space-y-2">
                    <div className="flex justify-between items-center bg-white p-2.5 rounded-xl border border-gray-200">
                        <div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Nimewo</p>
                            <p className="text-sm font-black text-gray-800">{info?.moncash_number}</p>
                        </div>
                        <button onClick={() => copy(info?.moncash_number)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="flex justify-between items-center bg-white p-2.5 rounded-xl border border-gray-200">
                        <div>
                            <p className="text-[9px] text-gray-400 font-bold uppercase">Kantite HTG</p>
                            <p className="text-sm font-black text-red-600">{parseInt(info?.amount_htg || 0).toLocaleString()} HTG</p>
                        </div>
                        <button onClick={() => copy(parseInt(info?.amount_htg || 0))} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><Copy className="w-3.5 h-3.5" /></button>
                    </div>
                </div>

                <Button
                    variant="outline"
                    onClick={verifyPayment}
                    disabled={isVerifying}
                    className="w-full h-10 rounded-xl border-gray-300 text-gray-600 font-bold text-xs"
                >
                    {isVerifying ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-2" />}
                    Tcheke Estati Peman
                </Button>
            </div>

            {/* WhatsApp */}
            <a href={`https://wa.me/${info?.whatsapp_number}?text=Bonjou%2C%20mwen%20vle%20rechaje%20kat%20Meru%20mwen.%20Referans%3A%20${info?.reference}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between w-full p-4 rounded-2xl hover:opacity-90 transition-opacity border-2 border-green-100"
                style={{ background: "rgba(37, 211, 102, 0.05)" }}>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white shadow-md">
                        <MessageCircle className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="font-black text-gray-800 text-sm">Voye Prèv sou WhatsApp</p>
                        <p className="text-gray-500 text-[10px]">Pataje screenshot la pou konfimasyon rapid</p>
                    </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
            </a>

            <Button onClick={onClose} className="w-full h-11 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200">
                Fèmen
            </Button>
        </div>
    );
};


// ===== MAIN MODAL =====
export const VirtualCardModal = ({ isOpen, onClose }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [serviceInfo, setServiceInfo] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (isOpen) { setStep(1); setResult(null); fetchServiceInfo(); }
    }, [isOpen]);

    const fetchServiceInfo = async () => {
        try {
            const { data } = await axios.get(`${API}/meru/info`);
            setServiceInfo(data);
        } catch (e) { console.error(e); }
    };

    const handleSubmit = async (formData) => {
        setLoading(true);
        const token = localStorage.getItem("buyer_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
            const { data } = await axios.post(`${API}/meru/recharge/request`, formData, { headers });
            setResult(data);
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erè nan soumèt demann nan");
        } finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
                <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl"
                    style={{ maxHeight: "95vh" }}
                    onClick={e => e.stopPropagation()}>

                    {/* Header */}
                    <div className="px-5 pt-5 pb-4 flex items-center justify-between"
                        style={{ background: "linear-gradient(135deg, #0d2b0d 0%, #1B6B1B 100%)" }}>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                style={{ background: "rgba(255,255,255,0.15)" }}>
                                <Wallet className="w-5 h-5 text-green-200" />
                            </div>
                            <div>
                                <h2 className="font-black text-white text-base">Rechaj Kat Meru</h2>
                                <p className="text-[10px] text-green-300">
                                    {step === 1 ? "Achte sou Amazon, Shein, eBay & plis..." : "Enstriksyon peman MonCash"}
                                </p>
                            </div>
                        </div>
                        <button onClick={onClose}
                            className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
                            <X className="w-4 h-4 text-white" />
                        </button>
                    </div>

                    {/* Step indicator */}
                    <div className="flex items-center gap-2 px-5 py-3 bg-green-50 border-b border-green-100">
                        {[{ n: 1, label: "Demann" }, { n: 2, label: "Peman" }].map(({ n, label }) => (
                            <div key={n} className="flex items-center gap-1.5">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${step >= n ? "text-white shadow-md" : "bg-gray-200 text-gray-400"}`}
                                    style={step >= n ? { background: "linear-gradient(135deg, #1B6B1B, #2E8B2E)" } : {}}>
                                    {step > n ? <CheckCircle className="w-3.5 h-3.5" /> : n}
                                </div>
                                <span className={`text-[11px] font-bold ${step >= n ? "text-green-700" : "text-gray-400"}`}>{label}</span>
                                {n < 2 && <ChevronRight className="w-3 h-3 text-gray-300" />}
                            </div>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="overflow-y-auto p-5" style={{ maxHeight: "calc(95vh - 130px)" }}>
                        {step === 1 && <StepForm onSubmit={handleSubmit} serviceInfo={serviceInfo} loading={loading} />}
                        {step === 2 && <StepPayment result={result} onClose={onClose} />}
                    </div>
                </div>
            </div>
        </>
    );
};

